import { type MixPanelEventMap, trackMixPanelEvent } from "$root/third-party-integrations/initMixPanel";
import type { ActionOf, WorkspaceAction, WorkspaceActionType } from "./state/action";
import { type WorkspaceState, resolveConcepts, sharedConcepts } from "./state/state";

/**
 * The content-mutating interactions surfaced to analytics. Kept as the single source of truth via the
 * event map so this union and the `workspace.content.change` payload can never drift apart.
 */
export type WorkspaceContentChangeTrigger = MixPanelEventMap["workspace.content.change"]["trigger"];

/**
 * What each operation reports — keyed on the *action*, not on the action type: three of the triggers depend
 * on the payload rather than on the operation.
 *
 * This is the whole interception mechanism. It replaced three parallel lists per controller — the methods,
 * the `excludeMethods` deny-list that separated reads from writes, and a method-name→trigger map — because
 * `dispatch` is the one interception point and a read is not an action. `satisfies` is what makes it
 * exhaustive: adding an operation without deciding how it is tracked is a compile error.
 *
 * **Three of the previous triggers have no action, and that is deliberate.** `addConcept`, `editConcept` and
 * `pruneUnusedConcepts` reported *consequences*: creating, editing and pruning a parameterized concept is
 * something an operation does, never something a user asks for. Two of the three already fired from nowhere;
 * the third, `addConcept`, had two live call sites — the chart panel minting a key-metric threshold concept —
 * and those now report as `addChart` / `editChart`, the operation the user actually performed. Recorded in
 * `spec.md` too, so whoever owns the dashboards learns it from the docs rather than from a flat line.
 */
export const workspaceActionTriggers = {
	// Where the concept lands is payload, but a custom property is a different *interaction* and has always
	// reported as one.
	addColumn: (action: ActionOf<"addColumn">) => ("property" in action ? "addCustomPropertyColumn" : "addColumn"),
	// A shared concept lives outside the workspace content, so editing one is not a content change — the two
	// methods this covers are exactly the two `useWorkspaceControllers` excludes from the observer.
	editColumn: (action: ActionOf<"editColumn">) =>
		"property" in action || action.shared === true ? null : ("editColumn" as const),
	// The attribute the option belongs to lives outside the workspace content, exactly as it does for an
	// `editColumn {property}`.
	renameTagOption: null,
	removeColumn: "removeColumn",
	// Same split as `actionSpec`, and for the same reason: a mid-drag sample is not an interaction the user has
	// finished, so it reports nothing. The settled layout the table re-dispatches when the grid stops moving
	// carries no flag and reports once — see the note on the action.
	reorderColumns: (action: ActionOf<"reorderColumns">) => (action.fromGrid ? null : ("setColumnsLayout" as const)),
	addFilter: "addFilter",
	// Upsert decides on its own whether it creates or edits, so it reports as itself rather than as one of
	// the two.
	upsertFilter: "upsertFilter",
	editFilter: "editFilter",
	removeFilter: "removeFilter",
	removeAllFilters: "removeAllFilters",
	addChart: "addChart",
	editChart: "editChart",
	removeChart: "removeChart",
	reorderCharts: "setChartsLayout",
	setView: "setView",
	// No controller today: the table header and the page-size select report these by hand, with a comment
	// apologising for it. Under dispatch they are ordinary actions.
	setSort: "setSort",
	setPageSize: "setPageSize",
	// Write-behind reconciliation of an entity the user already saw appear. The interaction that caused it
	// reported when it happened.
	sharedConceptsReplaced: null,
	conceptReconciled: null,
	// The criteria this rewrites are the rename propagating, not a filter the user edited.
	tagOptionRenamed: null,
	// A refetch echoing what some other screen did to the document. Whoever renamed or moved it reported that
	// where it happened; this is not a second interaction.
	documentRefreshed: null,
	// The ephemeral slice is not content, and none of it is tracked today.
	setSearch: null,
	setSelection: null,
	setPage: null,
	setResultTotal: null,
} satisfies Record<
	WorkspaceActionType,
	WorkspaceContentChangeTrigger | null | ((action: never) => WorkspaceContentChangeTrigger | null)
>;

/** What the action reports, `null` when it reports nothing. */
export function workspaceActionTrigger(action: WorkspaceAction): WorkspaceContentChangeTrigger | null {
	const decide = workspaceActionTriggers[action.type];
	return typeof decide === "function"
		? (decide as (a: WorkspaceAction) => WorkspaceContentChangeTrigger | null)(action)
		: decide;
}

/**
 * The event, with a snapshot of the content the user now sees.
 *
 * Called by the middleware *after* the state transition, which is what makes "the workspace as it is now"
 * true without anyone having to remember the ordering. A `null` trigger is a no-op, so the lookup above can
 * be passed straight in.
 */
export function trackWorkspaceStateChange(state: WorkspaceState, trigger: WorkspaceContentChangeTrigger | null): void {
	if (trigger == null) {
		return;
	}

	const columns = state.content.columns ?? [];
	const charts = state.content.charts;
	const conceptByUuid = new Map(resolveConcepts(state.concepts).map((concept) => [concept.uuid, concept]));

	trackMixPanelEvent("workspace.content.change", {
		trigger,
		workspace: { uuid: state.meta.document?.uuid, name: state.meta.document?.name },
		view: state.content.view,
		columns: columns.length,
		visibleColumns: columns.filter((column) => !column.hide).length,
		charts: charts.length,
		visibleCharts: charts.filter((chart) => !chart.preferences?.hide).length,
		filters: state.content.filters.length,
		privateConcepts: state.concepts.filter((entry) => entry.origin === "WORKSPACE").length,
		sharedConcepts: sharedConcepts(state.concepts).length,
		pageSize: state.content.pageSize,
		sort: state.content.sort,
		columnConcepts: columns.map((column) => ({
			uuid: column.colId,
			conceptId: conceptByUuid.get(column.colId)?.conceptId ?? "",
			hidden: Boolean(column.hide),
		})),
		chartSeries: charts.map((chart) => ({
			chartType: chart.chartType,
			series: chart.series.length,
			hidden: Boolean(chart.preferences?.hide),
		})),
	});
}
